import { NotificationDefinitionOptionsDto } from "../api/generated/models/NotificationDefinitionOptionsDto";
import { NotificationDefinitionOptionsViewModel } from "../api/generated/models/NotificationDefinitionOptionsViewModel";

/**
 * Flat form state for the audience filters of a segmented push definition.
 * Amounts are GrooKroner (kr, not øre); dates are `datetime-local` values
 * interpreted as UTC by the backend.
 */
export interface AudienceFormState {
  osEnabled: boolean;
  includeAndroid: boolean;
  includeIos: boolean;
  includeWeb: boolean;
  cardEnabled: boolean;
  hasCardAdded: boolean;
  balanceEnabled: boolean;
  balanceMin: string;
  balanceMax: string;
  lastPurchaseEnabled: boolean;
  lastPurchaseAfter: string;
  lastPurchaseBefore: string;
  usersEnabled: boolean;
  userIds: string[];
}

export const EMPTY_AUDIENCE_FORM: AudienceFormState = {
  osEnabled: false,
  includeAndroid: true,
  includeIos: true,
  includeWeb: false,
  cardEnabled: false,
  hasCardAdded: true,
  balanceEnabled: false,
  balanceMin: "",
  balanceMax: "",
  lastPurchaseEnabled: false,
  lastPurchaseAfter: "",
  lastPurchaseBefore: "",
  usersEnabled: false,
  userIds: [],
};

/** `datetime-local` value → the exact `yyyy-MM-ddTHH:mm:ssZ` format the backend validator accepts. */
const toBackendDate = (value: string): string | undefined => {
  if (!value) return undefined;
  const withSeconds = value.length === 16 ? `${value}:00` : value;
  return withSeconds.endsWith("Z") ? withSeconds : `${withSeconds}Z`;
};

/** Backend date string → `datetime-local` value. */
const toInputDate = (value: string | null | undefined): string =>
  value ? value.slice(0, 16) : "";

export const toOptionsDto = (
  form: AudienceFormState
): NotificationDefinitionOptionsDto => ({
  mobileOsOption: form.osEnabled
    ? {
        isEnabled: true,
        shouldIncludeAndroid: form.includeAndroid,
        shouldIncludeIos: form.includeIos,
        shouldIncludeWeb: form.includeWeb,
      }
    : { isEnabled: false },
  hasPaymentCardOption: form.cardEnabled
    ? { isEnabled: true, hasPaymentCardAdded: form.hasCardAdded }
    : { isEnabled: false },
  grooKronerAmountOption: form.balanceEnabled
    ? {
        isEnabled: true,
        grooKronerAmountMinimum: Number(form.balanceMin || 0),
        grooKronerAmountMaximum: Number(form.balanceMax || 0),
      }
    : { isEnabled: false },
  userLastActionTimeOption: form.lastPurchaseEnabled
    ? {
        isEnabled: true,
        userLastActionTimeAfter: toBackendDate(form.lastPurchaseAfter),
        userLastActionTimeBefore: toBackendDate(form.lastPurchaseBefore),
      }
    : { isEnabled: false },
  selectedUsersOption: form.usersEnabled
    ? { isEnabled: true, userIds: form.userIds }
    : { isEnabled: false },
});

export const fromOptionsViewModel = (
  options: NotificationDefinitionOptionsViewModel | undefined
): AudienceFormState => {
  const os = options?.mobileOsOption;
  const card = options?.hasPaymentCardOption;
  const balance = options?.grooKronerAmountOption;
  const lastPurchase = options?.userLastActionTimeOption;
  const users = options?.selectedUsersOption;

  return {
    osEnabled: os?.isEnabled ?? false,
    includeAndroid: os?.shouldIncludeAndroid ?? true,
    includeIos: os?.shouldIncludeIos ?? true,
    // The web flag is newer than the generated client; present in the response since the
    // conversion-tracking backend release
    includeWeb: (os as { shouldIncludeWeb?: boolean } | undefined)?.shouldIncludeWeb ?? false,
    cardEnabled: card?.isEnabled ?? false,
    hasCardAdded: card?.hasPaymentCardAdded ?? true,
    balanceEnabled: balance?.isEnabled ?? false,
    balanceMin:
      balance?.grooKronerAmountMinimum !== undefined
        ? String(balance.grooKronerAmountMinimum)
        : "",
    balanceMax:
      balance?.grooKronerAmountMaximum !== undefined
        ? String(balance.grooKronerAmountMaximum)
        : "",
    lastPurchaseEnabled: lastPurchase?.isEnabled ?? false,
    lastPurchaseAfter: toInputDate(lastPurchase?.userLastActionTimeAfter),
    lastPurchaseBefore: toInputDate(lastPurchase?.userLastActionTimeBefore),
    usersEnabled: users?.isEnabled ?? false,
    userIds: users?.userIds ?? [],
  };
};
